import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { allSettled } from 'effector';
import { useUnit } from 'effector-react';
import { Divider, Menu, MenuItem, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { GridSortDirection, GridSortModel } from '@mui/x-data-grid-pro';
import {
  $column,
  $columns,
  $config,
  $order,
  createSortToolbarScope,
  saveSortToStorage,
  setColumn,
  setOrder,
} from '../../model/model';
import { orders } from '../../types';
import { MenuHeader } from './styled';
import * as Styled from './styled';

export const SortMenu = () => {
  const { t } = useTranslation();
  const [columns, column, order, config] = useUnit([$columns, $column, $order, $config]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isFieldInColumns = !!columns.find(({ id }) => id === column)?.field;

  const sortField = isFieldInColumns
    ? columns.find(({ id }) => id === column)!.field
    : t('NotSet1');
  const SortOrder = () => {
    if (!isFieldInColumns) return null;
    if (!order) return null;

    return order === orders.asc ? (
      <ArrowUpwardIcon sx={{ fontSize: 18 }} fontSize="small" color="primary" />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 18 }} fontSize="small" color="primary" />
    );
  };

  const sortWasManuallyChanged = async (sort: GridSortModel) => {
    const scope = createSortToolbarScope(config!.id);
    await allSettled(saveSortToStorage, {
      scope,
      params: sort,
    });
  };

  const setColumnFunc = async (id: string | null) => {
    if (!config?.id) return;
    const scope = createSortToolbarScope(config!.id);

    await allSettled(setColumn, { scope, params: id });
    if (!order) await allSettled(setOrder, { scope, params: orders.asc });

    sortWasManuallyChanged(id ? [{ field: id, sort: order ?? orders.asc }] : []);
    handleClose();
  };

  const setOrderFunc = async (order: GridSortDirection) => {
    if (!config?.id) return;
    const scope = createSortToolbarScope(config!.id);

    await allSettled(setOrder, { scope, params: order });
    if (!column) await allSettled(setColumn, { scope, params: columns[0].id });

    sortWasManuallyChanged(
      order ? [{ field: column ?? columns[0].id, sort: order }] : []
    );
    handleClose();
  };

  const resetSort = async () => {
    if (!config?.id) return;
    const scope = createSortToolbarScope(config!.id);

    await allSettled(setColumn, { scope, params: null });
    await allSettled(setOrder, { scope, params: null });

    sortWasManuallyChanged([]);

    handleClose();
  };

  const somethingChoosen = order !== null && column !== null;

  return (
    <div>
      <MenuHeader>
        <Typography variant="body2" color="text.secondary">
          {t('SortBy')}:
        </Typography>

        <Styled.MainButton
          variant="text"
          size="small"
          color="primary"
          onClick={handleClick}
        >
          {sortField}

          <SortOrder />
        </Styled.MainButton>
      </MenuHeader>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {columns.map(({ id, field }) => (
          <MenuItem key={id} disableRipple onClick={() => setColumnFunc(id)}>
            {somethingChoosen && (
              <Styled.CheckedMark>
                {column === id ? <Styled.CheckIconCustom /> : null}
              </Styled.CheckedMark>
            )}

            {field}
          </MenuItem>
        ))}
        <Divider />

        <MenuItem disableRipple onClick={() => setOrderFunc(orders.asc)}>
          {somethingChoosen && (
            <Styled.CheckedMark>
              {order === orders.asc ? <Styled.CheckIconCustom /> : null}
            </Styled.CheckedMark>
          )}
          {t('Ascending')}
        </MenuItem>
        <MenuItem disableRipple onClick={() => setOrderFunc(orders.desc)}>
          {somethingChoosen && (
            <Styled.CheckedMark>
              {order === orders.desc ? <Styled.CheckIconCustom /> : null}
            </Styled.CheckedMark>
          )}
          {t('Descending')}
        </MenuItem>
        {config?.canReset && somethingChoosen && (
          <>
            <Divider />

            <MenuItem disableRipple onClick={() => resetSort()}>
              {t('ResetSorting')}
            </MenuItem>
          </>
        )}
      </Menu>
    </div>
  );
};
